import {
    SmartCallModalHeader,
    SmartCallProfile,
    SmartCallTab,
    SmartCallTabs,
    useSmartCallOutletContext,
} from './CloudConnectorSmartCall.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { FormContentLayout } from '../layout/components/form/FormLayout.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Alert, Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
    useServerErrorEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { OperatorTypo } from '../ui/OperatorTypo.ts';
import { AddToPhotos, Delete, Edit } from '@mui/icons-material';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import { DateTime } from 'luxon';
import { UseFormReturn, useWatch, useForm } from 'react-hook-form';
import {
    ValidationData,
    addValidationErrorsToForm,
} from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { css } from '@emotion/css';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import {
    hasFullDayConfigured,
    SmartCallTimeWindowConfigDisplay,
    SmartCallTimeWindowConfigEditDialog,
    SmartCallTimeWindowConfig,
} from './CloudConnectorSmartCallTimeWindowConfig.tsx';
import { GenericFormSubmitError } from '../parkingabo-users/ParkingaboUsersUtils.tsx';
import { GenericFormError } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';

const maxIndividualDateInFutureMonths = 18;
const pastIndividualDateRetentionDays = 90;

enum FormFields {
    dates = 'dates',
}

export type SmartCallIndividualDates = {
    [FormFields.dates]: IndividualDate[];
};

export type SmartCallIndividualDatesFieldValues = {
    [FormFields.dates]: SmartCallIndividualDateFieldValues[];
};

enum IndividualDateFields {
    key = 'key',
    date = 'date',
    windows = 'windows',
}

interface IndividualDate {
    [IndividualDateFields.date]: string;
    [IndividualDateFields.windows]: SmartCallTimeWindowConfig[];
}

interface SmartCallIndividualDateFieldValues {
    [IndividualDateFields.key]: string;
    [IndividualDateFields.date]: DateTime;
    [IndividualDateFields.windows]: SmartCallTimeWindowConfig[];
}

export function CloudConnectorSmartCallIndividualDatesTab() {
    const {
        zoneId,
        onClose,
        navigateWithinModal,
        profilesState,
        onDirtyStateChange,
        refetchProfiles,
    } = useSmartCallOutletContext();

    const [individualDatesState, refetchIndividualDates] = useServerFetch<
        SmartCallIndividualDates,
        { zoneId: string }
    >(
        ({ zoneId }) => ({
            url: `/ui-api/operator-account/cloud-connector/smart-call/zone/${zoneId}/individual-dates`,
        }),
        { zoneId: zoneId },
    );

    return (
        <OperatorAsyncLoadedSection
            requestState={individualDatesState}
            render={individualDates => (
                <CloudConnectorSmartCallIndividualDatesContent
                    individualDates={individualDates}
                    refetchIndividualDates={refetchIndividualDates}
                    zoneId={zoneId}
                    onDirtyStateChange={onDirtyStateChange}
                    onClose={onClose}
                    profilesState={profilesState}
                    navigateWithinModal={navigateWithinModal}
                    refetchProfiles={refetchProfiles}
                />
            )}
        />
    );
}

function CloudConnectorSmartCallIndividualDatesContent({
    individualDates,
    refetchIndividualDates,
    zoneId,
    onDirtyStateChange,
    onClose,
    profilesState,
    navigateWithinModal,
    refetchProfiles,
}: {
    individualDates: SmartCallIndividualDates;
    refetchIndividualDates: () => void;
    zoneId: string;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
    onClose: () => void;
    profilesState: ReturnType<
        typeof useSmartCallOutletContext
    >['profilesState'];
    navigateWithinModal: ReturnType<
        typeof useSmartCallOutletContext
    >['navigateWithinModal'];
    refetchProfiles: () => void;
}) {
    const [editState, putIndividualDates] = useServerWrite<
        SmartCallIndividualDates,
        object,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/cloud-connector/smart-call/zone/${zoneId}/individual-dates`,
        method: RequestMethod.PUT,
    }));

    useServerSuccessEffect(editState, () => {
        refetchIndividualDates();
        refetchProfiles();
    });

    const { language } = useLanguage();

    const form = useForm<SmartCallIndividualDatesFieldValues>({
        defaultValues: {
            [FormFields.dates]: toIndividualDateFieldValues(
                individualDates[FormFields.dates],
            ),
        },
    });

    const [genericSubmitError, setGenericSubmitError] =
        useState<GenericFormError | null>(null);
    useServerErrorEffect(editState, (statusCode, data) => {
        if (statusCode === 400 && data !== null) {
            const globalError = addValidationErrorsToForm(
                data,
                form.setError,
                language,
            );
            setGenericSubmitError({
                isGenericServerError: false,
                issues: globalError,
            });
        } else {
            setGenericSubmitError({
                isGenericServerError: true,
            });
        }
    });

    const isDirty = form.formState.isDirty;

    useEffect(() => {
        onDirtyStateChange(isDirty);
    }, [isDirty, onDirtyStateChange]);

    return (
        <OperatorRoutedModalContent
            header={
                <SmartCallModalHeader
                    editMode={isDirty}
                    buttons={
                        isDirty ? (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    refetchIndividualDates();
                                }}
                                onSave={form.handleSubmit(
                                    (
                                        payload: SmartCallIndividualDatesFieldValues,
                                    ) =>
                                        putIndividualDates({
                                            [FormFields.dates]:
                                                toIndividualDates(
                                                    payload[FormFields.dates],
                                                ),
                                        }),
                                )}
                                loading={
                                    editState.status === RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <HeaderActionCloseButtons onClose={onClose} />
                        )
                    }
                />
            }
            bar={
                <SmartCallTabs
                    smartCallTab={SmartCallTab.INDIVIDUAL_DATES}
                    navigateWithinModal={navigateWithinModal}
                />
            }
            style={{ padding: 0 }}
            body={
                <OperatorAsyncLoadedSection
                    requestState={profilesState}
                    render={(profiles: SmartCallProfile[]) => (
                        <>
                            {genericSubmitError != null && (
                                <GenericFormSubmitError
                                    error={genericSubmitError}
                                    submitState={editState}
                                    sx={{
                                        marginBottom: 0,
                                    }}
                                />
                            )}
                            <SmartCallIndividualDatesForm
                                form={form}
                                profiles={profiles}
                            />
                        </>
                    )}
                />
            }
        />
    );
}

function SmartCallIndividualDatesForm({
    form,
    profiles,
}: {
    form: UseFormReturn<SmartCallIndividualDatesFieldValues>;
    profiles: SmartCallProfile[];
}) {
    const datesFields = useWatch({
        control: form.control,
        name: FormFields.dates,
    });
    const [editModalField, setEditModalField] =
        useState<SmartCallIndividualDateFieldValues | null>(null);
    const [createModalField, setCreateModalField] =
        useState<SmartCallIndividualDateFieldValues | null>(null);
    const [deleteDateField, setDeleteDateField] =
        useState<SmartCallIndividualDateFieldValues | null>(null);

    return (
        <>
            <Box
                sx={{
                    backgroundColor: theme => theme.palette.blue.medium,
                    height: 55,
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: theme => theme.spacing(0, 3),
                }}
            >
                <Box
                    style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const newDate = {
                                [IndividualDateFields.key]: crypto.randomUUID(),
                                [IndividualDateFields.date]:
                                    findNextAvailableDate(datesFields),
                                [IndividualDateFields.windows]: [],
                            };

                            setCreateModalField(newDate);
                        }}
                    >
                        <Localized
                            de="Hinzufügen"
                            fr="Ajouter"
                            it="Aggiungi"
                            en="Add"
                        />
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    height: '100%',
                    color: theme => theme.palette.blue.main,
                    padding: '20px 24px',
                    ...Typo.robotoMedium,
                }}
            >
                {editModalField !== null && (
                    <DateEditModal
                        field={editModalField}
                        onClose={() => setEditModalField(null)}
                        profiles={profiles}
                        form={form}
                    />
                )}
                {createModalField !== null && (
                    <DateCreateModal
                        field={createModalField}
                        onClose={() => setCreateModalField(null)}
                        profiles={profiles}
                        form={form}
                    />
                )}
                {deleteDateField !== null && (
                    <DeleteDateConfirmDialog
                        date={formatDateLabel(
                            deleteDateField[IndividualDateFields.date],
                        )}
                        onCancel={() => setDeleteDateField(null)}
                        onConfirm={() => {
                            form.setValue(
                                FormFields.dates,
                                sortDates(
                                    datesFields.filter(
                                        date =>
                                            date[IndividualDateFields.key] !==
                                            deleteDateField[
                                                IndividualDateFields.key
                                            ],
                                    ),
                                ),
                                {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                },
                            );
                            setDeleteDateField(null);
                        }}
                    />
                )}
                <FormContentLayout>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                            }}
                        >
                            {datesFields.map(date => (
                                <DateSegment
                                    key={date[IndividualDateFields.key]}
                                    field={date}
                                    profiles={profiles}
                                    onEdit={() => setEditModalField(date)}
                                    onDelete={() => setDeleteDateField(date)}
                                    onDuplicate={() => {
                                        const sourceDate = datesFields.find(
                                            existingDate =>
                                                existingDate[
                                                    IndividualDateFields.key
                                                ] ===
                                                date[IndividualDateFields.key],
                                        );
                                        if (!sourceDate) {
                                            return;
                                        }

                                        const duplicatedDate = {
                                            ...sourceDate,
                                            [IndividualDateFields.key]:
                                                crypto.randomUUID(),
                                            [IndividualDateFields.date]:
                                                findNextAvailableDate(
                                                    datesFields,
                                                ),
                                            [IndividualDateFields.windows]:
                                                sourceDate[
                                                    IndividualDateFields.windows
                                                ].map(windowConfig => ({
                                                    ...windowConfig,
                                                })),
                                        };
                                        setCreateModalField(duplicatedDate);
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </FormContentLayout>
            </Box>
        </>
    );
}

function DateEditModal({
    field,
    onClose,
    profiles,
    form,
}: {
    field: SmartCallIndividualDateFieldValues;
    onClose: () => void;
    profiles: SmartCallProfile[];
    form: UseFormReturn<SmartCallIndividualDatesFieldValues>;
}) {
    return (
        <SmartCallTimeWindowConfigEditDialog
            profiles={profiles}
            timeWindowConfigs={field[IndividualDateFields.windows]}
            onConfirm={timeWindowConfigs => {
                form.setValue(
                    FormFields.dates,
                    sortDates(
                        form.watch(FormFields.dates).map(dateField =>
                            dateField[IndividualDateFields.key] ===
                            field[IndividualDateFields.key]
                                ? {
                                      ...dateField,
                                      [IndividualDateFields.windows]:
                                          timeWindowConfigs,
                                  }
                                : dateField,
                        ),
                    ),
                    {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                    },
                );

                onClose();
            }}
            onCancel={onClose}
            title={<>{formatDateLabel(field[IndividualDateFields.date])}</>}
        />
    );
}

function DateCreateModal({
    field,
    onClose,
    profiles,
    form,
}: {
    field: SmartCallIndividualDateFieldValues;
    onClose: () => void;
    profiles: SmartCallProfile[];
    form: UseFormReturn<SmartCallIndividualDatesFieldValues>;
}) {
    const dates = useWatch({
        control: form.control,
        name: FormFields.dates,
    });
    const [selectedDate, setSelectedDate] = useState(
        field[IndividualDateFields.date],
    );
    const minDate = DateTime.now().plus({ days: 1 });
    const maxDate = DateTime.now().plus({
        months: maxIndividualDateInFutureMonths,
    });

    return (
        <SmartCallTimeWindowConfigEditDialog
            profiles={profiles}
            timeWindowConfigs={field[IndividualDateFields.windows]}
            onConfirm={timeWindowConfigs => {
                form.setValue(
                    FormFields.dates,
                    sortDates([
                        ...form.watch(FormFields.dates),
                        {
                            ...field,
                            [IndividualDateFields.date]: selectedDate,
                            [IndividualDateFields.windows]: timeWindowConfigs,
                        },
                    ]),
                    {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                    },
                );

                onClose();
            }}
            onCancel={onClose}
            title={
                <Localized
                    de="Neuer Tag"
                    fr="Nouveau jour"
                    it="Nuovo giorno"
                    en="New day"
                />
            }
        >
            <Box sx={{ paddingY: '10px' }}>
                <DesktopDatePicker
                    label={
                        <Localized de="Tag" fr="Jour" it="Giorno" en="Day" />
                    }
                    format="dd.MM.yyyy"
                    value={selectedDate}
                    onChange={date => {
                        if (date) {
                            setSelectedDate(date);
                        }
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                    shouldDisableDate={date =>
                        isIndividualDateUsed(dates, date)
                    }
                />
            </Box>
        </SmartCallTimeWindowConfigEditDialog>
    );
}

function DateSegment({
    field,
    profiles,
    onEdit,
    onDelete,
    onDuplicate,
}: {
    field: SmartCallIndividualDateFieldValues;
    profiles: SmartCallProfile[];
    onEdit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
}) {
    const configuredTimeWindows = field[IndividualDateFields.windows].filter(
        window => window.smartCallProfileId && window.start && window.end,
    );
    const hasWindows = configuredTimeWindows.length > 0;
    const defaultProfile = profiles.find(profile => profile.isDefault);
    const fullDayConfigured = hasFullDayConfigured(configuredTimeWindows);

    if (!defaultProfile) {
        return (
            <Alert severity="error">
                <Localized
                    de="Das Standard-Profil fehlt"
                    fr="Le profil par défaut est manquant"
                    it="Il profilo predefinito è mancante"
                    en="The default profile is missing"
                />
            </Alert>
        );
    }

    const isPastDate =
        field[IndividualDateFields.date].startOf('day') <
        DateTime.now().startOf('day');

    return (
        <Box sx={{ width: '50%', paddingRight: 3 }}>
            <DateConfigHeader
                field={field}
                isPastDate={isPastDate}
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
            />
            <Box
                sx={{
                    margin: '24px',
                    color: theme =>
                        isPastDate
                            ? theme.palette.grey.A400
                            : theme.palette.blue.main,
                }}
            >
                {hasWindows ? (
                    <Box>
                        {configuredTimeWindows.map(timeWindowConfig => (
                            <SmartCallTimeWindowConfigDisplay
                                key={timeWindowConfig.start}
                                timeWindow={timeWindowConfig}
                                profiles={profiles}
                            />
                        ))}
                        {!fullDayConfigured && (
                            <Box sx={{ marginTop: '10px' }}>
                                <Localized
                                    de={`Ausserhalb: ${defaultProfile.description}`}
                                    fr={`En dehors: ${defaultProfile.description}`}
                                    it={`Al di fuori: ${defaultProfile.description}`}
                                    en={`Outside: ${defaultProfile.description}`}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <>{defaultProfile.description}</>
                )}
            </Box>
        </Box>
    );
}

function DeleteDateConfirmDialog({
    date,
    onConfirm,
    onCancel,
}: {
    date: string;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={true}
            title={
                <Localized
                    de="Tag löschen"
                    fr="Supprimer le jour"
                    it="Cancellare giorno"
                    en="Delete day"
                />
            }
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={onConfirm}
            onCancel={onCancel}
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <Localized
                de={
                    <p>
                        Wollen Sie wirklich das Datum <b>{date}</b> löschen?
                    </p>
                }
                fr={
                    <p>
                        Voulez-vous vraiment supprimer la date du <b>{date}</b>?
                    </p>
                }
                it={
                    <p>
                        Vuole veramente eliminare il giorno <b>{date}</b>?
                    </p>
                }
                en={
                    <p>
                        Do you really want to delete the date <b>{date}</b>?
                    </p>
                }
            />
        </ConfirmationDialog>
    );
}

function toIndividualDateFieldValues(
    dates: IndividualDate[],
): SmartCallIndividualDateFieldValues[] {
    return sortDates(
        dates
            .map<SmartCallIndividualDateFieldValues>(date => ({
                ...date,
                [IndividualDateFields.key]: crypto.randomUUID(),
                [IndividualDateFields.date]: DateTime.fromISO(
                    date[IndividualDateFields.date],
                ),
            }))
            .filter(date => isRetainedDate(date[IndividualDateFields.date])),
    );
}

function formatDateLabel(date: DateTime): string {
    return date.toFormat('EEEE, dd.MM.yyyy');
}

function toIndividualDates(
    dates: SmartCallIndividualDateFieldValues[],
): IndividualDate[] {
    return dates
        .filter(date => isRetainedDate(date[IndividualDateFields.date]))
        .map(date => ({
            [IndividualDateFields.date]:
                date[IndividualDateFields.date].toISODate()!,
            [IndividualDateFields.windows]: date[IndividualDateFields.windows],
        }));
}

function isIndividualDateUsed(
    dateFields: SmartCallIndividualDateFieldValues[],
    date: DateTime,
): boolean {
    const isoDate = date.toISODate();
    if (!isoDate) {
        return false;
    }

    return dateFields.some(dateField => {
        return dateField[IndividualDateFields.date].toISODate() === isoDate;
    });
}

function sortDates(
    dates: SmartCallIndividualDateFieldValues[],
): SmartCallIndividualDateFieldValues[] {
    return [...dates].sort(
        (a, b) =>
            b[IndividualDateFields.date].toMillis() -
            a[IndividualDateFields.date].toMillis(),
    );
}

function findNextAvailableDate(
    dates: SmartCallIndividualDateFieldValues[],
): DateTime {
    let date = DateTime.now().plus({ days: 1 });

    while (
        dates.some(dateField =>
            dateField[IndividualDateFields.date].hasSame(date, 'day'),
        )
    ) {
        date = date.plus({ days: 1 });
    }

    return date;
}

function isRetainedDate(date: DateTime): boolean {
    return (
        date.startOf('day') >=
        DateTime.now()
            .minus({ days: pastIndividualDateRetentionDays })
            .startOf('day')
    );
}

function DateConfigHeader({
    field,
    isPastDate,
    onEdit,
    onDelete,
    onDuplicate,
}: {
    field: SmartCallIndividualDateFieldValues;
    isPastDate: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
}) {
    const canModify =
        field[IndividualDateFields.date].startOf('day') >
        DateTime.now().startOf('day');

    return (
        <Box
            sx={{
                ...OperatorTypo.headingOne,
                fontWeight: '600',
                color: theme =>
                    isPastDate
                        ? theme.palette.grey.A400
                        : theme.palette.blue.main,
                backgroundColor: theme => theme.palette.grey.A200,
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <Box sx={{ margin: '2px 8px' }}>
                {formatDateLabel(field[IndividualDateFields.date])}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    paddingX: 1,
                    alignItems: 'center',
                    color: theme => theme.palette.blue.main,
                }}
            >
                {canModify && (
                    <>
                        <Clickable
                            element={'div'}
                            onClick={onEdit}
                            className={css({ display: 'flex', margin: '2px' })}
                        >
                            <Edit />
                        </Clickable>
                    </>
                )}
                <Clickable
                    element={'div'}
                    onClick={onDuplicate}
                    className={css({ display: 'flex', margin: '2px' })}
                >
                    <AddToPhotos />
                </Clickable>
                {canModify && (
                    <>
                        <Clickable
                            element={'div'}
                            onClick={onDelete}
                            className={css({ display: 'flex', margin: '2px' })}
                        >
                            <Delete />
                        </Clickable>
                    </>
                )}
            </Box>
        </Box>
    );
}
